'use client';

import { ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';

import { playSfx } from '@/hooks/use-sound';
import { trackEvent } from '@/lib/events';
import { cn } from '@/lib/utils';

import { ROLLOUT_STAGES } from './data';

/**
 * The five-stage rollout on the site's vertical timeline rail (the same
 * geometry as the experience section): one node per stage, each a true
 * disclosure. The first stage starts open so the shape of the story is
 * visible without a click.
 */
export function RolloutTimeline() {
  const [openId, setOpenId] = useState<string | null>(ROLLOUT_STAGES[0].id);

  const toggle = (id: string) => {
    const isOpening = openId !== id;
    playSfx(isOpening ? 'open' : 'close');
    setOpenId(isOpening ? id : null);
    if (isOpening) {
      trackEvent({
        name: 'case_study_rollout_stage',
        properties: { stage: id },
      });
    }
  };

  return (
    <div className="p-4">
      <ol className="relative m-0 flex list-none flex-col p-0 before:absolute before:top-5 before:bottom-0 before:left-3 before:w-px before:bg-border">
        {ROLLOUT_STAGES.map((stage) => {
          const open = openId === stage.id;

          return (
            <li
              key={stage.id}
              // The experiences-rail idiom: the last item paints a background
              // mask over the rail so it ends at the final node, even with
              // that node's panel expanded.
              className="relative last:before:absolute last:before:top-5 last:before:left-0 last:before:h-full last:before:w-4 last:before:bg-background"
            >
              <button
                type="button"
                aria-expanded={open}
                aria-controls={`stage-panel-${stage.id}`}
                data-sfx-managed=""
                onClick={() => toggle(stage.id)}
                className={cn(
                  'relative flex w-full items-start gap-3 py-2 text-left',
                  'before:absolute before:-inset-y-0.5 before:-right-1 before:left-7 before:-z-1 before:rounded-lg before:transition-[background-color] before:ease-out hover:before:bg-accent2'
                )}
              >
                <span
                  aria-hidden
                  className="relative z-1 flex size-6 shrink-0 items-center justify-center"
                >
                  <span
                    className={cn(
                      'size-2 rounded-full transition-colors',
                      open ? 'bg-foreground' : 'bg-zinc-300 dark:bg-zinc-600'
                    )}
                  />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <span className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase tabular-nums">
                      {stage.date}
                    </span>
                    <span className="text-sm font-medium">{stage.name}</span>
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {stage.summary}
                  </span>
                </span>

                <ChevronDownIcon
                  aria-hidden
                  className={cn(
                    'mt-1 size-4 shrink-0 text-muted-foreground transition-transform duration-300',
                    open && 'rotate-180'
                  )}
                />
              </button>

              <div
                id={`stage-panel-${stage.id}`}
                className={cn(
                  'grid transition-[grid-template-rows] duration-300 ease-out',
                  open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                )}
              >
                <div className="overflow-hidden" inert={!open}>
                  <div className="pt-1 pr-6 pb-3 pl-9">
                    <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
                      {stage.meta}
                    </p>
                    <p className="mt-2 max-w-[70ch] text-sm text-muted-foreground">
                      {stage.body}
                    </p>
                    <p className="mt-2 font-mono text-xs text-muted-foreground">
                      verified · {stage.gate}
                    </p>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
